// One-time survey on the dashboard home: which POS and which staff planning
// tool a restaurant uses. One answer per restaurant, stored at
// restaurants/{restaurantId}/surveyResponses/{TOOLS_SURVEY_ID}; the Firestore
// rules allow creating it once and never changing it.

export const TOOLS_SURVEY_ID = 'tools-2026-11';
export const OTHER_OPTION = 'other';
export const MAX_OTHER_LENGTH = 80;
export const MAX_NAME_LENGTH = 120;

export type SurveyOption = {
  value: string;
  label: string;
  // Brand names stay as they are; descriptive labels are translation keys.
  translateLabel?: boolean;
  // Brands show their logo in the grid; "other" and "none" sit below it.
  logo?: string;
};

const brand = (value: string, label: string, translateLabel = false): SurveyOption => ({
  value,
  label,
  translateLabel,
  logo: `/survey-logos/${value}.png`,
});

export const POS_OPTIONS: SurveyOption[] = [
  brand('lightspeed', 'Lightspeed (incl. former Gastrofix)', true),
  brand('orderbird', 'orderbird'),
  brand('vectron', 'Vectron'),
  brand('gastronovi', 'gastronovi'),
  brand('ready2order', 'ready2order'),
  brand('apro', 'APRO'),
  brand('3pos', '3POS'),
  brand('schultes', 'Schultes (bluepos)'),
  brand('hypersoft', 'Hypersoft'),
  brand('lina', 'LINA (formerly Amadeus 360)', true),
  brand('hellotess', 'helloTESS'),
  brand('sumup', 'SumUp'),
  { value: OTHER_OPTION, label: 'Other', translateLabel: true },
  { value: 'none', label: 'No electronic POS', translateLabel: true },
];

export const STAFF_PLANNING_OPTIONS: SurveyOption[] = [
  brand('e2n', 'e2n'),
  brand('ordio', 'Ordio'),
  brand('gastromatic', 'gastromatic'),
  brand('gastronovi', 'gastronovi'),
  brand('dyflexis', 'Dyflexis'),
  brand('papershift', 'Papershift'),
  brand('planday', 'Planday'),
  brand('shiftbase', 'Shiftbase'),
  { value: OTHER_OPTION, label: 'Other', translateLabel: true },
  { value: 'none', label: 'No tool (Excel, paper, messenger)', translateLabel: true },
];

export type SurveyAnswer = { choice: string; other: string };

export type SurveyQuestion = 'pos' | 'staffPlanning';

export type ToolsSurveyAnswers = Record<SurveyQuestion, SurveyAnswer>;

export const toolsSurveyAnswersPath = (restaurantId: string) =>
  `restaurants/${restaurantId}/surveyResponses`;

export const EMPTY_ANSWER: SurveyAnswer = { choice: '', other: '' };

export const isAnswerComplete = ({ choice, other }: SurveyAnswer) =>
  !!choice && (choice !== OTHER_OPTION || other.trim().length > 0);

const otherText = ({ choice, other }: SurveyAnswer) =>
  choice === OTHER_OPTION ? other.trim().slice(0, MAX_OTHER_LENGTH) : null;

export type ToolsSurveyResponse = {
  surveyId: string;
  restaurantId: string;
  pos: string;
  posOther: string | null;
  staffPlanning: string;
  staffPlanningOther: string | null;
  answeredByUid: string;
  answeredByName: string;
};

export const buildToolsSurveyResponse = ({
  restaurantId,
  uid,
  userName,
  answers,
}: {
  restaurantId: string;
  uid: string;
  userName: string;
  answers: ToolsSurveyAnswers;
}): ToolsSurveyResponse => ({
  surveyId: TOOLS_SURVEY_ID,
  restaurantId,
  pos: answers.pos.choice,
  posOther: otherText(answers.pos),
  staffPlanning: answers.staffPlanning.choice,
  staffPlanningOther: otherText(answers.staffPlanning),
  answeredByUid: uid,
  answeredByName: userName.trim().slice(0, MAX_NAME_LENGTH),
});

// Closing the survey hides it for the rest of the local calendar day.
export const localDay = (now: Date = new Date()) =>
  [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

const dismissalKey = (uid: string) =>
  `gastronaut_survey_dismissed_v1:${TOOLS_SURVEY_ID}:${uid}`;

export const isDismissedToday = (
  storage: Storage,
  uid: string,
  now: Date = new Date(),
) => {
  try {
    return storage.getItem(dismissalKey(uid)) === localDay(now);
  } catch {
    return false;
  }
};

export const dismissForToday = (
  storage: Storage,
  uid: string,
  now: Date = new Date(),
) => {
  try {
    storage.setItem(dismissalKey(uid), localDay(now));
  } catch {
    // Without storage the survey simply shows again on the next visit.
  }
};

// Shared tablet logins carry role 'tablet'. The `tablet` flag alone is a
// permission level that admins have as well.
export const isSharedTabletAccount = (membership: { role?: string } | null) =>
  membership?.role === 'tablet';

export type ToolsSurveyVisibility = {
  restaurantId: string | null;
  uid: string | null;
  rolesLoaded: boolean;
  isSharedTablet: boolean;
  isGastronautAdmin: boolean;
  responseLoading: boolean;
  responseExists: boolean;
  responseError: string | null;
  dismissedToday: boolean;
  otherDialogOpen: boolean;
};

// Personal restaurant accounts only: shared tablets and Gastronaut staff would
// answer for a restaurant they do not run. Anything unknown keeps it hidden.
export const shouldShowToolsSurvey = (state: ToolsSurveyVisibility) =>
  !!state.restaurantId &&
  !!state.uid &&
  state.rolesLoaded &&
  !state.isSharedTablet &&
  !state.isGastronautAdmin &&
  !state.responseLoading &&
  !state.responseError &&
  !state.responseExists &&
  !state.dismissedToday &&
  !state.otherDialogOpen;
